import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import FlagLoader from "layouts/flagloader";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { apiErrorMessage, apiSuccessMessage } from "utils/apiResponseMessages";

const PAGE_SIZE = 5;

const LoginApprovalNotifications = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [comments, setComments] = useState({});
  const [processing, setProcessing] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );

  const can = useCallback(
    (...requiredPermissions) => hasPermission(grantedPermissions, ...requiredPermissions),
    [grantedPermissions]
  );

  const canViewPage = can("login_approvals.pages.list.view");
  const canViewField = useCallback(
    (field) => can(`login_approvals.fields.${field}.view`),
    [can]
  );

  const canDecide = useCallback(
    (notification, approve) => {
      const type = notification?.type || "";
      const action = approve ? "approve" : "reject";

      return can(`login_approvals.actions.${action}_${type}`);
    },
    [can]
  );

  const fetchNotifications = useCallback(async () => {
    if (!canViewPage) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(`${API_URL}/login-approval-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications(Array.isArray(res.data?.notifications) ? res.data.notifications : []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(error.response?.data, "Failed to fetch notifications"),
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [canViewPage]);

  useEffect(() => {
    if (!isAuthReady) return undefined;

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);

    return () => clearInterval(interval);
  }, [fetchNotifications, isAuthReady]);

  const handleDecision = async (notification, approve) => {
    const comment = (comments[notification.id] || "").trim();

    if (!comment) {
      setSnackbar({
        open: true,
        message: "Comment is required.",
        color: "error",
      });
      return;
    }

    if (!canDecide(notification, approve)) {
      setSnackbar({
        open: true,
        message: "You do not have permission for this action.",
        color: "error",
      });
      return;
    }

    if (!approve && !window.confirm("Are you sure you want to reject this request?")) {
      return;
    }

    try {
      setProcessing((prev) => ({ ...prev, [notification.id]: true }));
      const token = localStorage.getItem("authToken");

      const res = await axios.post(
        `${API_URL}/login-approval-notifications/${notification.attendance_request_id}/decision`,
        { approve, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: apiSuccessMessage(
          res.data,
          approve ? "Request approved successfully." : "Request rejected successfully."
        ),
        color: "success",
      });

      setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
      setComments((prev) => {
        const updated = { ...prev };
        delete updated[notification.id];
        return updated;
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: apiErrorMessage(error.response?.data, "Approval action failed"),
        color: "error",
      });
    } finally {
      setProcessing((prev) => ({ ...prev, [notification.id]: false }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(notifications.length / PAGE_SIZE));
  const paginatedData = notifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!isAuthReady) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h6">Login approval access is not assigned.</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Login Approvals"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />
      <SoftBox p={3}>
        <SoftTypography variant="h4" fontWeight="bold" mb={2}>
          Login Approval Requests
        </SoftTypography>

        {loading ? (
          <SoftBox display="flex" justifyContent="center" py={4}>
            <FlagLoader />
          </SoftBox>
        ) : notifications.length === 0 ? (
          <SoftTypography>No login approval requests found.</SoftTypography>
        ) : (
          <>
            {paginatedData.map((notification) => {
              const busy = Boolean(processing[notification.id]);
              const isEarly = notification.type === "early_login";
              const typeLabel = isEarly ? "early login" : "late login";

              return (
                <SoftBox
                  key={notification.id}
                  mt={2}
                  p={2}
                  border="1px solid #d7dee8"
                  borderRadius="lg"
                  sx={{ backgroundColor: "#f8f9fa" }}
                >
                  <SoftTypography>
                    {canViewField("requester") && (
                      <>
                        <strong>{notification.user_name}</strong>{" "}
                      </>
                    )}
                    requested <strong>{typeLabel}</strong> approval
                    {canViewField("shift") && (
                      <>
                        {" "}
                        for shift <strong>{notification.shift_type}</strong>
                      </>
                    )}
                    {canViewField("sub_branch") && (
                      <>
                        {" "}
                        from <strong>{notification.sub_branch_name}</strong>
                      </>
                    )}
                  </SoftTypography>

                  <SoftTypography variant="body2" mt={1}>
                    {canViewField("minutes") && (
                      <>
                        Minutes: {notification.minutes} <br />
                      </>
                    )}
                    {canViewField("scheduled_start") && (
                      <>
                        Scheduled Start: {notification.scheduled_start || "-"} <br />
                      </>
                    )}
                    {canViewField("request_login_time") && (
                      <>
                        Requested Login Time: {notification.request_login_time || "-"} <br />
                      </>
                    )}
                    {canViewField("request_comment") && (
                      <>Reason: {notification.request_comment || "-"}</>
                    )}
                  </SoftTypography>

                  {canViewField("decision_comment") && (
                    <TextField
                      fullWidth
                      placeholder="Add a comment"
                      value={comments[notification.id] || ""}
                      onChange={(event) =>
                        setComments((prev) => ({
                          ...prev,
                          [notification.id]: event.target.value,
                        }))
                      }
                      sx={{ mt: 2 }}
                      disabled={busy}
                    />
                  )}

                  {canViewField("actions") && (
                    <SoftBox mt={2} display="flex" gap={1} flexWrap="wrap">
                      {canDecide(notification, true) && (
                        <SoftButton
                          color="success"
                          size="small"
                          onClick={() => handleDecision(notification, true)}
                          disabled={busy}
                        >
                          {busy ? <CircularProgress size={18} color="inherit" /> : "Approve"}
                        </SoftButton>
                      )}
                      {canDecide(notification, false) && (
                        <SoftButton
                          color="error"
                          size="small"
                          onClick={() => handleDecision(notification, false)}
                          disabled={busy}
                        >
                          {busy ? <CircularProgress size={18} color="inherit" /> : "Reject"}
                        </SoftButton>
                      )}
                    </SoftBox>
                  )}
                </SoftBox>
              );
            })}

            {totalPages > 1 && (
              <SoftBox mt={3} display="flex" justifyContent="center" alignItems="center" gap={2}>
                <SoftButton
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  Prev
                </SoftButton>
                <SoftTypography variant="body2">
                  Page {page} of {totalPages}
                </SoftTypography>
                <SoftButton
                  variant="outlined"
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  Next
                </SoftButton>
              </SoftBox>
            )}
          </>
        )}
      </SoftBox>
    </DashboardLayout>
  );
};

export default LoginApprovalNotifications;
